import { z } from 'zod';
import {
  entityIdSchema,
  moneySchema,
  projectViewSchema,
} from './current-project';

export const cursorSchema = moneySchema;
const timestamp = z.number().int().nonnegative().safe().max(8_640_000_000_000);
export const notificationViewSchema = z.object({
  notificationId: cursorSchema.refine((id) => id !== '0'),
  event: z.object({
    providerInstanceId: entityIdSchema,
    cursor: cursorSchema,
    operationId: entityIdSchema,
    kind: z.enum([
      'WorkerRegistered',
      'WorkerUpdated',
      'CalendarUpdated',
      'CatalogUpdated',
      'CoordinatorPromoted',
      'ScorePolicyUpdated',
      'AccountFunded',
      'ProjectCreated',
      'PlanningQuoted',
      'PlanningAccepted',
      'ProposalCreated',
      'ProposalUpdated',
      'ProposalDeleted',
      'ProposalSubmitted',
      'PlanningCompleted',
      'ExecutionApproved',
      'ProposalChangesRequested',
      'ProjectCancelled',
      'PlanningDisputed',
      'MilestoneDisputed',
      'TaskCreated',
      'TaskUpdated',
      'TaskDeleted',
      'ProposalWithdrawn',
      'ProjectEvaluated',
      'MilestoneCompletionRequested',
      'MilestoneCompleted',
      'MilestoneCompletionRejected',
      'DisputeOpened',
      'DisputeResponseAdded',
      'DepositRequested',
      'DepositConfirmed',
      'WithdrawalRequested',
      'WithdrawalCancelled',
      'SkillRequested',
      'SkillRequestApproved',
      'SkillRequestRejected',
    ]),
    projectId: entityIdSchema.nullable(),
    entityId: entityIdSchema.nullable(),
    recipients: z.array(projectViewSchema.shape.client),
    occurredAt: timestamp,
    origin: projectViewSchema.shape.client.nullable().optional(),
  }),
  actor: z
    .object({
      accountId: projectViewSchema.shape.client,
      displayName: z.string(),
      imageUrl: z
        .string()
        .regex(/^\/api\/profiles\/(0x)?[0-9a-f]{32}\/(client|worker)\/image$/)
        .nullable(),
    })
    .nullable()
    .default(null),
  projectTitle: z.string().nullable().default(null),
  readAt: timestamp.nullable(),
});
export type NotificationView = z.infer<typeof notificationViewSchema>;
// Preserve integer tokens before Number rounding. Older browsers can read safe
// integers; unsafe tokens fail explicitly instead of rounding identifiers.
export function notificationJSON(text: string): unknown {
  return JSON.parse(
    text,
    (key: string, value: unknown, context?: { source: string }) => {
      if (
        ['notificationId', 'nextCursor', 'cursor'].includes(key) &&
        typeof value === 'number'
      ) {
        if (context?.source) return context.source;
        if (!Number.isSafeInteger(value))
          throw new Error(
            'This browser cannot preserve the notification cursor. Use a current browser.'
          );
        return String(value);
      }
      return value;
    }
  );
}
export function parseNotification(text: string, account: string) {
  const notification = notificationViewSchema.parse(notificationJSON(text));
  if (!notification.event.recipients.includes(account))
    throw new Error('The server returned a notification for another account.');
  return notification;
}
export function notificationLink(notification: NotificationView) {
  const { kind, projectId, entityId } = notification.event;
  if (entityId && ['DisputeOpened', 'DisputeResponseAdded'].includes(kind))
    return '/disputes/' + entityId;
  if (entityId && /^(Deposit|Withdrawal)/.test(kind))
    return (
      '/payments/fund?kind=' +
      (kind.startsWith('Deposit') ? 'deposit' : 'withdrawal') +
      '&request=' +
      entityId
    );
  if (projectId) {
    const path = '/projects/' + projectId;
    if (kind === 'ProposalSubmitted') return path + '?review=scope';
    // Completion events identify a submission, not a milestone.
    if (kind === 'MilestoneCompletionRequested' && entityId)
      return path + '?submission=' + entityId;
    return path;
  }
  if (
    [
      'WorkerRegistered',
      'WorkerUpdated',
      'CalendarUpdated',
      'CoordinatorPromoted',
      'SkillRequested',
      'SkillRequestApproved',
      'SkillRequestRejected',
    ].includes(kind)
  )
    return '/profile';
  if (kind === 'AccountFunded') return '/payments';
  return null;
}
